/**
 * German-locale formatting helpers.
 *
 * - Numbers use the standard `Intl.NumberFormat("de-DE")` (grouping like 4.812).
 * - Dates / relative time use **date-fns** with the German locale, so we don't
 *   hand-roll (and hand-test) calendar math.
 * - The `*IsoUtc` builders construct half-open query bounds and are deliberately
 *   kept explicit — they must stay in UTC (date-fns operates in local time).
 */
import { format } from "date-fns/format";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { differenceInHours } from "date-fns/differenceInHours";
import { differenceInDays } from "date-fns/differenceInDays";
import { differenceInMonths } from "date-fns/differenceInMonths";
import { differenceInYears } from "date-fns/differenceInYears";

const numberFmt = new Intl.NumberFormat("de-DE");

/** 4812 → "4.812". */
export function formatNumberDe(value: number): string {
  return numberFmt.format(value);
}

/** Signed percent for trend pills: 12 → "12", used as "▲ 12 %". */
export function formatPercentDe(value: number): string {
  return numberFmt.format(Math.round(value));
}

/**
 * "yyyy-mm-dd" (from an `<input type="date">`) → ISO at UTC midnight of that day.
 * The backend's `DateTime` scalar rejects date-only literals, so custom-range
 * bounds must be sent as full ISO timestamps.
 */
export function dayStartIsoUtc(date: string): string {
  return `${date}T00:00:00.000Z`;
}

/**
 * "yyyy-mm-dd" → ISO at UTC midnight of the *next* day. Used as the exclusive
 * upper bound so an inclusive "Bis <day>" actually includes that whole day
 * (the backend window is half-open `[from, to)`).
 */
export function dayEndExclusiveIsoUtc(date: string): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

/** ISO ("2026-07-15" or full date-time) → "15.07." (day + month, trailing dot). */
export function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "dd.MM.");
}

/** A `Date` → "14:20" (24h, zero-padded). */
export function formatTimeDe(date: Date): string {
  return format(date, "HH:mm");
}

/** ISO → "15.07.2026". */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "dd.MM.yyyy");
}

/**
 * Compact German relative-time string for a past ISO timestamp:
 * "gerade eben", "vor 2 Min.", "vor 2 Std.", "vor 7 Tagen", "vor 3 Mon.", "vor 1 J.".
 *
 * No "week" bucket — days count up to 30, then months. The compact labels are
 * ours; the calendar-aware differences (esp. months/years) come from date-fns.
 */
export function formatRelativeDe(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();

  const minutes = differenceInMinutes(now, d);
  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `vor ${minutes} Min.`;

  const hours = differenceInHours(now, d);
  if (hours < 24) return `vor ${hours} Std.`;

  const days = differenceInDays(now, d);
  if (days < 30) return `vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;

  const months = differenceInMonths(now, d);
  if (months < 12) return `vor ${months} Mon.`;

  return `vor ${differenceInYears(now, d)} J.`;
}
